// DS2 Numeric: a number field (react-number-format, "." thousands and "," decimals) with an optional suffix (the unit)
// and stepper inside the box. The value goes out as a number: onValueChange on every change, onBlur with the last
// value (commit on blur). Only digits / separators / "-" can be typed; Delete is kept from the table rows around it.
// controls: the stepper, by scale, rounded, not below min.
import cx from "classnames";
import { useId, useRef, type CSSProperties, type KeyboardEvent, type MouseEvent, type Ref } from "react";
import {
  NumericFormat,
  type InputAttributes,
  type NumberFormatValues,
  type NumericFormatProps,
} from "react-number-format";
import useI18n from "../../hooks/useTranslations";
import { ArrowDropDownIcon, ArrowDropUpIcon } from "./icons";
import styles from "./Numeric.module.css";

export type NumericControls = {
  scale: number;
  onUpdate: (value: number) => void;
};

export type NumericProps = Omit<
  NumericFormatProps<InputAttributes>,
  "onChange" | "onValueChange" | "onBlur" | "max"
> & {
  isInvalid?: boolean;
  controls?: NumericControls;
  min?: number;
  onBlur?: (value: number | null) => void;
  onValueChange?: (value: number | null) => void;
  // the input's width, e.g. "50px" (the stepper is beside it)
  width?: string;
  ref?: Ref<HTMLInputElement>;
};

const ALLOWED_CONTROL_KEYS = new Set([
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Backspace",
  "Delete",
  "End",
  "Enter",
  "Escape",
  "Home",
  "Tab",
]);

const toNumber = (value: NumericProps["value"]): number | null => {
  if (value === undefined || value === null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isNaN(n) ? null : n;
};

export function Numeric({
  isInvalid,
  controls,
  min,
  suffix,
  width,
  className,
  onValueChange,
  ref,
  ...rest
}: NumericProps) {
  const generatedId = useId();
  const t = useI18n().translation.designSystem;
  const allowDecimalSeparator = rest.decimalScale !== 0;
  const lastChange = useRef<NumberFormatValues | undefined>(undefined);
  const changed = useRef(false);

  const isAllowedPrintableKey = (key: string) =>
    /^\d$/.test(key) ||
    (allowDecimalSeparator && (key === "," || key === ".")) ||
    (!!rest.allowNegative && key === "-");

  const handleStep = (event: MouseEvent<HTMLButtonElement>, delta: number) => {
    event.preventDefault();
    if (!controls || rest.disabled) return;
    const next = stepValue(toNumber(rest.value), delta, min);
    if (next !== undefined) controls.onUpdate(next);
  };

  const style: CSSProperties = { ...rest.style, ...(width ? { width, flex: "none" } : undefined) };

  return (
    <div
      className={cx(
        styles.numeric,
        isInvalid && styles.invalid,
        rest.disabled && styles.disabled,
        width && styles.fitted,
        className,
      )}
    >
      <NumericFormat
        {...rest}
        id={rest.id ?? generatedId}
        value={rest.value === undefined || rest.value === null ? "" : rest.value}
        getInputRef={ref}
        thousandSeparator="."
        decimalSeparator=","
        inputMode={rest.inputMode ?? (allowDecimalSeparator ? "decimal" : "numeric")}
        autoComplete={rest.autoComplete ?? "off"}
        spellCheck={rest.spellCheck ?? false}
        aria-invalid={isInvalid || undefined}
        className={styles.input}
        style={style}
        onValueChange={(value) => {
          changed.current = true;
          lastChange.current = value;
          onValueChange?.(value.floatValue ?? null);
        }}
        onBlur={() => {
          const value = changed.current ? (lastChange.current?.floatValue ?? null) : toNumber(rest.value);
          changed.current = false;
          lastChange.current = undefined;
          rest.onBlur?.(value);
        }}
        onChange={() => {
          // the value goes out as a number, through onValueChange / onBlur
        }}
        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
          const control = event.ctrlKey || event.metaKey || event.altKey || ALLOWED_CONTROL_KEYS.has(event.key);
          if (!control && event.key.length === 1 && !isAllowedPrintableKey(event.key)) {
            event.preventDefault();
            return;
          }
          // Delete edits the field, not the table row around it
          if (event.key === "Delete") event.stopPropagation();
          rest.onKeyDown?.(event);
        }}
      />
      {suffix ? <span className={styles.suffix}>{suffix}</span> : null}
      {controls ? (
        <span className={styles.stepper}>
          {[1, -1].map((sign) => (
            <button
              key={sign}
              type="button"
              className={styles.step}
              disabled={rest.disabled}
              title={sign > 0 ? t.increaseLabel(controls.scale) : t.decreaseLabel(controls.scale)}
              onClick={(event) => handleStep(event, sign * controls.scale)}
            >
              {sign > 0 ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
            </button>
          ))}
        </span>
      ) : null}
    </div>
  );
}

function stepValue(value: number | string | null | undefined, delta: number, min?: number): number | undefined {
  const current = value === undefined || value === null || value === "" ? 0 : Number(value);
  const decimals = `${value}`.split(".")[1];
  const factor = (decimals ? Math.pow(10, decimals.length) : 1) * 10;
  const next = Math.round(((Number.isNaN(current) ? 0 : current) + delta) * factor) / factor;
  return min === undefined || next >= min ? next : undefined;
}
